import React from 'react';
import { MessageSquare, Calendar, Palette, Undo, Redo, Home, Download, Users } from 'lucide-react';
import { useT } from './i18n/index';

// ── SidebarButton ─────────────────────────────────────────────────────────────

export function SidebarButton({ onClick, title, color, hoverColor: _hoverColor, active: _active = false, disabled = false, children, ...rest }: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title: string;
  color: string;
  hoverColor?: string;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  [key: string]: any;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 40,
        padding: '6px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        background: disabled ? '#C5B9B3' : color,
        border: 'none',
        borderRadius: 6,
        color: disabled ? '#7a6f6a' : 'white',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 9,
        opacity: disabled ? 0.5 : 1,
        transition: 'opacity 0.15s',
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CanvasSidebarProps {
  onAddBox:    () => void;
  onAddNote:   () => void;
  onAddStart:  () => void;

  showLegend:      boolean;
  onToggleLegend:  () => void;

  showResources:     boolean;
  onToggleResources: () => void;

  isDrawingAreaMode:  boolean;
  onToggleAreaMode:   () => void;

  onUndo: () => void;
  onRedo: () => void;

  showCriticalPath:     boolean;
  onToggleCriticalPath: () => void;

  canExport:      boolean;
  onExportClick:  (e: React.MouseEvent<HTMLButtonElement>) => void;

  currentId:          string | null;
  defaultFlowchartId: string | null;
  onToggleHome:       () => void;

  onSwitchToMindmap?: () => void;
  version: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CanvasSidebar(props: CanvasSidebarProps) {
  const {
    onAddBox, onAddNote, onAddStart,
    showLegend, onToggleLegend,
    showResources, onToggleResources,
    isDrawingAreaMode, onToggleAreaMode,
    onUndo, onRedo,
    showCriticalPath, onToggleCriticalPath,
    canExport, onExportClick,
    currentId, defaultFlowchartId, onToggleHome,
    onSwitchToMindmap, version,
  } = props;

  const { t } = useT();
  const isHome = currentId === defaultFlowchartId;

  return (
    <div style={{
      width: 56,
      background: '#EDE7E3',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 12,
      paddingBottom: 'env(safe-area-inset-bottom, 12px)',
      gap: 4,
      flexShrink: 0,
      boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
      overflowY: 'auto',
    }}>
      <span style={{ fontSize: 9, color: '#16697A', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{t('addLabel')}</span>

      <SidebarButton data-tour="btn-box" onClick={onAddBox} title={t('addBox')} color="#489FB5">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="4" />
        </svg>
        <span>{t('box')}</span>
      </SidebarButton>

      <SidebarButton data-tour="btn-note" onClick={onAddNote} title={t('addNote')} color="#FFA62B">
        <MessageSquare size={16} />
        <span>{t('note')}</span>
      </SidebarButton>

      <SidebarButton data-tour="btn-start" onClick={onAddStart} title={t('addStartDay')} color="#16697A">
        <Calendar size={16} />
        <span>{t('start')}</span>
      </SidebarButton>

      <SidebarButton data-tour="btn-category" onClick={onToggleLegend} title={t('toggleCategory')} color={showLegend ? '#16697A' : '#82C0CC'} active={showLegend}>
        <Palette size={16} />
        <span>{t('category')}</span>
      </SidebarButton>

      <SidebarButton data-tour="btn-resources" onClick={onToggleResources} title={t('toggleResources')} color={showResources ? '#16697A' : '#82C0CC'} active={showResources}>
        <Users size={16} />
        <span>{t('resources')}</span>
      </SidebarButton>

      <SidebarButton
        onClick={onToggleAreaMode}
        title={isDrawingAreaMode ? t('areaModeActiveHint') : t('areaModeHint')}
        color={isDrawingAreaMode ? '#16697A' : '#82C0CC'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2" />
          <line x1="8" y1="8" x2="13" y2="8" />
        </svg>
        <span>{t('area')}</span>
      </SidebarButton>

      <div style={{ width: 32, height: 1, background: '#C5B9B3', margin: '6px 0' }} />
      <span style={{ fontSize: 9, color: '#16697A', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{t('editLabel')}</span>

      <SidebarButton onClick={onUndo} title={t('undoHint')} color="#489FB5">
        <Undo size={16} />
        <span>{t('undo')}</span>
      </SidebarButton>

      <SidebarButton onClick={onRedo} title={t('redoHint')} color="#489FB5">
        <Redo size={16} />
        <span>{t('redo')}</span>
      </SidebarButton>

      <SidebarButton
        onClick={onToggleCriticalPath}
        title={t('showCriticalPathHint')}
        color={showCriticalPath ? '#f08080' : '#82C0CC'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="7" height="5" rx="1" />
          <rect x="15" y="16" width="7" height="5" rx="1" />
          <path d="M9 5.5 L15 5.5 L15 18.5" />
        </svg>
        <span>{t('critical')}</span>
      </SidebarButton>

      <div style={{ flex: 1 }} />

      <SidebarButton onClick={onExportClick} title={t('export')} color="#489FB5" disabled={!canExport}>
        <Download size={16} />
        <span>{t('export')}</span>
      </SidebarButton>

      <SidebarButton
        onClick={onToggleHome}
        disabled={!currentId}
        title={!currentId ? t('homeSaveFirstHint') : isHome ? t('homeRemoveHint') : t('homeSetHint')}
        color={isHome ? '#FFA62B' : '#82C0CC'}
      >
        <Home size={16} />
        <span>{isHome ? t('homeActive') : t('home')}</span>
      </SidebarButton>

      <div style={{ width: 32, height: 1, background: '#C5B9B3', margin: '4px 0' }} />

      {/* Flowchart (active) */}
      <div
        title={t('flowchartModeActive')}
        style={{ width: 40, padding: '6px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: '#16697A', borderRadius: 6, color: 'white', fontSize: 8 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="8" y="14" width="8" height="7" rx="1" />
          <path d="M6.5 10v2M17.5 10v2M6.5 12h11M12 12v2" />
        </svg>
        <span>{t('flowMode')}</span>
      </div>

      <button
        onClick={onSwitchToMindmap}
        title={t('switchToMindmap')}
        style={{ width: 40, padding: '6px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: '#C5B9B3', border: '1px solid #B3A9A4', borderRadius: 6, color: '#16697A', cursor: 'pointer', fontSize: 8 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="3.5" r="1.5" /><line x1="12" y1="5" x2="12" y2="9" />
          <circle cx="12" cy="20.5" r="1.5" /><line x1="12" y1="15" x2="12" y2="19" />
          <circle cx="3.5" cy="12" r="1.5" /><line x1="5" y1="12" x2="9" y2="12" />
          <circle cx="20.5" cy="12" r="1.5" /><line x1="15" y1="12" x2="19" y2="12" />
        </svg>
        <span>{t('mindMode')}</span>
      </button>

      <span style={{ fontSize: 13, color: '#16697A', fontWeight: 500, letterSpacing: '0.03em', marginTop: 4 }}>v{version}</span>
    </div>
  );
}
